import React, { useRef, useState } from 'react';
import { Formik, Field, FormikProps } from 'formik';
import * as Yup from 'yup';
import Modal from '$/components/global/modal';
import BixButton from '$/components/global/bixButton';
import InputBlock from '$/components/global/inputBlock';
import { ComplianceService } from '$/services';
import * as Bixby from '$/core';
import './index.scss';

interface IDeclarationRequestProps {
  company_Id: string;
  company_Name: string;
  handleRequestDeclarationClose: () => Promise<void>;
  handleRequestDeclarationSubmit: () => Promise<void>;
  hasAutoApproval: boolean;
}

interface DeclarationRequestValues {
  issuerHasPubliclyTradedSecurities: boolean | null;
  userHasManagerOrInvestmentCommitteeApproval: boolean | null;
}

export const DeclarationRequest: React.FC<IDeclarationRequestProps> = props => {
  const { company_Id, company_Name, handleRequestDeclarationSubmit, handleRequestDeclarationClose, hasAutoApproval } =
    props;

  const [isRequestingDeclaration, setIsRequestingDeclaration] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const initialValues = {
    issuerHasPubliclyTradedSecurities: null,
    userHasManagerOrInvestmentCommitteeApproval: null,
  } as DeclarationRequestValues;

  const formikRef = useRef<FormikProps<DeclarationRequestValues>>(null);

  const handleCloseModal = () => {
    if (formikRef && formikRef.current) {
      formikRef.current.resetForm();
    }
    setIsRequestingDeclaration(false);
    setError(null);
    handleRequestDeclarationClose();
  };

  const handleRequestDeclaration = async (values: DeclarationRequestValues, resetForm: () => void) => {
    setError(null);

    try {
      setIsRequestingDeclaration(true);
      await ComplianceService.requestDeclaration({
        company_Id,
        issuerHasPubliclyTradedSecurities: values.issuerHasPubliclyTradedSecurities,
        userHasManagerOrInvestmentCommitteeApproval: values.userHasManagerOrInvestmentCommitteeApproval,
      } as Bixby.Models.CreateDeclarationRequest);
      handleRequestDeclarationSubmit();
      resetForm();
    } catch (err) {
      setError('Unable to request declaration');
    } finally {
      setIsRequestingDeclaration(false);
    }
  };

  const RadioBox = ({ id, name, label, checked, ...props }: { [key: string]: any }) => (
    <div className="bix_radio_btn">
      <input type="radio" id={id} name={name} checked={checked} {...props} />
      <label htmlFor={id}>
        <span className="checked" />
        {label}
      </label>
    </div>
  );

  const schema = Yup.object({
    issuerHasPubliclyTradedSecurities: Yup.boolean(),
    userHasManagerOrInvestmentCommitteeApproval: Yup.boolean()
      .nullable()
      .when('issuerHasPubliclyTradedSecurities', {
        is: true,
        then: schema => schema.required('Manager or investment committee approval required.'),
      }),
  });

  return (
    <Modal title="Declaration Request" active forForm hideClose>
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={schema}
        // tslint:disable-next-line: jsx-no-lambda
        onSubmit={async (values: DeclarationRequestValues, { resetForm }) => {
          await handleRequestDeclaration(values, resetForm);
        }}
        render={({ handleSubmit, values, setFieldValue }) => (
          <form className="declaration-request-form" onSubmit={handleSubmit}>
            <p className="declaration-note">
              Please confirm you would like to request a private declaration for {company_Name}.
              {!hasAutoApproval && (
                <span>
                  &nbsp;A request will be sent to your compliance administrator to approve or deny this declaration.
                </span>
              )}
            </p>

            <InputBlock title="Does the issuer have publicly traded securities?">
              <Field
                name="issuerHasPubliclyTradedSecurities"
                render={({ field }: { [key: string]: any }) => {
                  return (
                    <div className="radio_button_group">
                      <RadioBox
                        {...field}
                        checked={values.issuerHasPubliclyTradedSecurities === true}
                        onChange={() => {
                          setFieldValue('issuerHasPubliclyTradedSecurities', true);
                          setFieldValue('userHasManagerOrInvestmentCommitteeApproval', null);
                        }}
                        label="Yes"
                        id="issuerHasPubliclyTradedSecuritiesYes"
                      />
                      <RadioBox
                        {...field}
                        checked={values.issuerHasPubliclyTradedSecurities === false}
                        onChange={() => {
                          setFieldValue('issuerHasPubliclyTradedSecurities', false);
                          setFieldValue('userHasManagerOrInvestmentCommitteeApproval', null);
                        }}
                        label="No"
                        id="issuerHasPubliclyTradedSecuritiesNo"
                      />
                    </div>
                  );
                }}
              />
            </InputBlock>
            {values.issuerHasPubliclyTradedSecurities && (
              <InputBlock title="Do you have manager or investment committee approval?">
                <Field
                  name="userHasManagerOrInvestmentCommitteeApproval"
                  render={({ field }: { [key: string]: any }) => {
                    return (
                      <div className="radio_button_group">
                        <RadioBox
                          {...field}
                          checked={values.userHasManagerOrInvestmentCommitteeApproval === true}
                          onChange={() => setFieldValue('userHasManagerOrInvestmentCommitteeApproval', true)}
                          label="Yes"
                          id="userHasManagerOrInvestmentCommitteeApprovalYes"
                        />
                        <RadioBox
                          {...field}
                          checked={values.userHasManagerOrInvestmentCommitteeApproval === false}
                          onChange={() => setFieldValue('userHasManagerOrInvestmentCommitteeApproval', false)}
                          label="No"
                          id="userHasManagerOrInvestmentCommitteeApprovalNo"
                        />
                      </div>
                    );
                  }}
                />
              </InputBlock>
            )}

            {error && <p className="error">{error}</p>}

            <div className="button_side_wrap">
              <BixButton
                type="submit"
                useLoading
                isLoading={isRequestingDeclaration}
                disabled={
                  isRequestingDeclaration ||
                  values.issuerHasPubliclyTradedSecurities === null ||
                  (values.issuerHasPubliclyTradedSecurities === true &&
                    values.userHasManagerOrInvestmentCommitteeApproval === null) ||
                  (values.issuerHasPubliclyTradedSecurities === true &&
                    values.userHasManagerOrInvestmentCommitteeApproval === false)
                }>
                Confirm
              </BixButton>
              <BixButton light type="button" onClick={() => handleCloseModal()} disabled={isRequestingDeclaration}>
                Cancel
              </BixButton>
            </div>
          </form>
        )}
      />
    </Modal>
  );
};
